import React from 'react';
import { Box, Container, Stack, Typography } from '@mui/material';
import {
  Dashboard,
  Warning,
  Report,
  People,
  Business,
  Height,
  SwapHoriz,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import type { LandingCopy, LandingPalette } from './landingCopy';

type ModulesSectionProps = {
  totalLhdForms: number;
  totalHandovers: number;
  copy: LandingCopy['modules'];
  palette: LandingPalette;
};

const iconByKey: Record<string, React.ReactNode> = {
  lhd: <Height sx={{ color: '#60A5FA' }} />,
  handover: <SwapHoriz sx={{ color: '#4ADE80' }} />,
  dashboard: <Dashboard sx={{ color: '#7DD3FC' }} />,
  incidents: <Warning sx={{ color: '#FBBF24' }} />,
  users: <People sx={{ color: '#93C5FD' }} />,
  reports: <Report sx={{ color: '#34D399' }} />,
};

const ModulesSection: React.FC<ModulesSectionProps> = ({
  totalLhdForms,
  totalHandovers,
  copy,
  palette,
}) => {
  const metaFor = (key: string, meta?: string, metaSuffix?: string) => {
    if (key === 'lhd') return `${totalLhdForms} ${metaSuffix ?? ''}`.trim();
    if (key === 'handover') return `${totalHandovers} ${metaSuffix ?? ''}`.trim();
    return meta ?? '';
  };

  return (
    <Box
      id="modules"
      component="section"
      sx={{
        py: { xs: 4, md: 5 },
        background: `linear-gradient(180deg, ${palette.sectionAlt} 0%, ${palette.pageBg} 100%)`,
        color: palette.text,
      }}
    >
      <Container maxWidth="lg">
        <Typography
          sx={{
            fontFamily: '"Outfit", "Cairo", sans-serif',
            fontWeight: 700,
            fontSize: { xs: '1.75rem', md: '2.25rem' },
            mb: 1,
          }}
        >
          {copy.title}
        </Typography>
        <Typography sx={{ color: palette.muted, mb: 2.5, maxWidth: 560 }}>
          {copy.subtitle}
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '1.15fr 0.85fr' },
            gap: 3,
            alignItems: 'stretch',
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2.5,
            }}
          >
            {copy.items.map((mod, index) => (
              <motion.div
                key={mod.key}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ delay: index * 0.05, duration: 0.4 }}
              >
                <Box
                  sx={{
                    p: 3,
                    height: '100%',
                    borderRadius: 3,
                    border: `1px solid ${palette.border}`,
                    background: palette.cardBg,
                    transition: 'border-color 0.25s ease, transform 0.25s ease',
                    '&:hover': {
                      borderColor: 'rgba(125,211,252,0.35)',
                      transform: 'translateY(-3px)',
                    },
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                    {iconByKey[mod.key]}
                    <Typography sx={{ fontFamily: '"Outfit", "Cairo", sans-serif', fontWeight: 700 }}>
                      {mod.title}
                    </Typography>
                  </Stack>
                  <Typography
                    variant="body2"
                    sx={{ color: palette.muted, mb: 2, lineHeight: 1.6 }}
                  >
                    {mod.description}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Business sx={{ fontSize: 14, color: palette.mutedSoft }} />
                    <Typography variant="caption" sx={{ color: palette.accent }}>
                      {metaFor(mod.key, 'meta' in mod ? mod.meta : undefined, 'metaSuffix' in mod ? mod.metaSuffix : undefined)}
                    </Typography>
                  </Stack>
                </Box>
              </motion.div>
            ))}
          </Box>

          <Box
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              border: `1px solid ${palette.border}`,
              minHeight: { xs: 320, lg: '100%' },
              order: { xs: -1, lg: 0 },
            }}
          >
            <Box
              component="img"
              src="/avi-elog-cover.jfif"
              alt={copy.posterAlt}
              sx={{
                width: '100%',
                height: '100%',
                minHeight: { xs: 320, lg: 560 },
                objectFit: 'cover',
                objectPosition: 'center',
                display: 'block',
              }}
            />
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default ModulesSection;
